/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  Linking,
  Modal,
} from "react-native";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useNetworkStatus } from "../services/networkStatus";
import { useFirestoreCacheStatus, useAuthStore, useUsersStore, useSettings, useStripeTestMode } from "../stores";
import { signOut } from "firebase/auth";
import { auth } from "../firebase.config";
import { isAdminBadgeLevel } from "../utils/pinConfig";
import { colors } from "../theme";

const AuthHeader = ({
  title,
  subtitle,
  isDesktop,
  isAuthenticated,
  shopInfo,
  storeLogo,
  // Defaults on so every existing caller keeps the bar it has today.
  showHelperBar = true,
}) => {
  const navigate = useNavigate();
  const { selectedUser, startSwitchUser } = useAuthStore();
  const { currentUser, users } = useUsersStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const isOnline = useNetworkStatus((state) => state.isOnline);
  const isServingCache = useFirestoreCacheStatus((state) => state.isServingCache);
  const settings = useSettings((state) => state.settings);
  const useTestStripe = useStripeTestMode((state) => state.useTestStripe);
  
  // Detect Safari (all versions - desktop and iOS)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const menuItems = [
    { label: "Calendar", path: "/calendar", icon: "🗓️" },
    { label: "Today", path: "/today", icon: "📍" },
    // Staff-only stepped booking flow. This menu only renders when
    // isAuthenticated, so no extra gating is needed here.
    { label: "Booking Wizard", path: "/booking-wizard", icon: "🪄" },
    // Sectioned edit flow: pick a booking, change one part at a time, one save.
    { label: "Edit Booking Wizard", path: "/edit-booking-wizard", icon: "✏️" },
    { label: "All Bookings", path: "/bookings", icon: "📋" },
    { label: "Email", path: "/gmail-inbox", icon: "📧" },
    { label: "Locks", path: "/locks", icon: "🔒" },
  ];

  const handleNavigate = (path) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleLogoutClick = () => {
    setMenuOpen(false);
    setShowLogoutConfirm(true);
  };

  // Drops the staff identity but keeps the Firebase session, so the PIN pad is
  // all that stands between users — no trip back to email/password.
  //
  // flushSync mounts the pad — and lets PinBoxes focus its first box — before
  // this tap handler returns. iOS raises the keypad for a focus() only while
  // the tap is still being processed; a render later is too late.
  const handleSwitchUser = () => {
    setMenuOpen(false);
    flushSync(() => startSwitchUser("switch"));
    navigate("/");
  };

  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      // Clear selectedUser from Zustand and localStorage
      const { logout } = useAuthStore.getState();
      logout();
      
      // Sign out from Firebase
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    }
    return phone;
  };

  const formatAddress = (address) => {
    if (!address) return "";
    // Remove zip code (5 digits or 5+4 format) and country (USA, United States, etc.)
    let formatted = address
      .replace(/,?\s*\d{5}(-\d{4})?\s*,?\s*/g, "") // Remove zip code
      .replace(/,?\s*(USA|United States|US)\s*$/i, "") // Remove country
      .trim();
    
    // Clean up any trailing commas
    formatted = formatted.replace(/,\s*$/, "");
    
    return formatted;
  };


  const handleAddressClick = () => {
    setInfoOpen(false);
    navigate("/map-viewer", { state: { address: shopInfo?.address } });
  };

  const handlePhoneClick = () => {
    if (shopInfo?.phone) {
      const cleanPhone = shopInfo.phone.replace(/\D/g, "");
      Linking.openURL(`tel:+1${cleanPhone}`);
    }
  };

  const handleEmailClick = async () => {
    if (shopInfo?.email) {
      try {
        // Copy to clipboard
        await navigator.clipboard.writeText(shopInfo.email);
        
        // Close the info dropdown
        setInfoOpen(false);
        
        // Show success message using native browser alert
        window.alert(
          `📧 Email Copied!\n\n${shopInfo.email} has been copied to your clipboard.\n\nYou can now open your email app of choice and paste the address.`
        );
      } catch (error) {
        console.error("Failed to copy email:", error);
        window.alert("❌ Error: Failed to copy email to clipboard");
      }
    }
  };

  return (
    <>
      <View style={[styles.headerWrapper, isServingCache && styles.headerWrapperOffline, useTestStripe && styles.headerWrapperTestMode]}>
        {useTestStripe && (
          <View style={styles.testModeBanner}>
            <Text style={styles.testModeBannerText}>STRIPE TEST MODE</Text>
          </View>
        )}
        <View style={[styles.header, isDesktop && styles.headerDesktop]}>
        {/* Logo */}
        {(storeLogo || "/bblogo_trans_high.png") && (
          <Image
            source={{ uri: storeLogo || "/bblogo_trans_high.png" }}
            style={styles.logo}
            resizeMode="contain"
          />
        )}

        {/* Page Title and Subtitle */}
        <View style={styles.titleContainer}>
          <Text style={styles.title}>{title}</Text>
          {subtitle && <Text style={styles.subtitle}>{subtitle}</Text>}
        </View>

        {/* Offline Data Indicator - shows when Firestore is serving cached data */}
        {isServingCache && (
          <View style={styles.offlineDataBanner}>
            <Text style={styles.offlineDataText}>Offline Data</Text>
          </View>
        )}

        {/* Offline Indicator */}
        {!isOnline && (
          <View style={styles.offlineIndicator}>
            <Text style={styles.offlineIcon}>📡</Text>
            <Text style={styles.offlineText}>Offline</Text>
          </View>
        )}

        {/* Hamburger Menu Button OR Info Icon */}
        {isAuthenticated ? (
          <View style={styles.menuContainer}>
            {/* Who is signed in — desktop only. The phone header is already tight
                on width, and the name would crowd the title out there. */}
            {isDesktop && (() => {
              const stored = selectedUser || currentUser;
              // selectedUser is a localStorage snapshot taken at PIN time, so its
              // authLevel goes stale after a promotion. Prefer the live record.
              const user = (stored?.id && users?.[stored.id]) || stored;
              const firstName = user?.firstName || user?.first_name || "";
              const lastName = user?.lastName || user?.last_name || "";
              const displayName = `${firstName} ${lastName}`.trim();
              if (!displayName) return null;

              return (
                <Text style={styles.userName} numberOfLines={1}>
                  {displayName}
                  {isAdminBadgeLevel(user?.authLevel) && (
                    <Text style={styles.userNameAdmin}> (admin)</Text>
                  )}
                </Text>
              );
            })()}
            
            {/* Bug Report Button */}
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.bugButton}
              onPress={() => navigate("/bug-report")}
            >
              <Text style={styles.bugIcon}>🐛</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={styles.hamburger}
              onPress={() => setMenuOpen(!menuOpen)}
            >
              <View style={styles.hamburgerLine} />
              <View style={styles.hamburgerLine} />
              <View style={styles.hamburgerLine} />
            </TouchableOpacity>
            
            {/* Dropdown Menu */}
            {menuOpen && (
              <View style={styles.menuDropdown}>
                {menuItems.map((item, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[
                      styles.menuItem,
                      index === menuItems.length - 1 && styles.menuItemLast
                    ]}
                    onPress={() => handleNavigate(item.path)}
                  >
                    <Text style={styles.menuIcon}>{item.icon}</Text>
                    <Text style={styles.menuText}>{item.label}</Text>
                  </TouchableOpacity>
                ))}

                {/* Separator Line */}
                <View style={styles.menuSeparator} />

                {/* Admin Button */}
                <TouchableOpacity
                  style={[styles.menuItem, styles.logoutSectionItem]}
                  onPress={() => {
                    setMenuOpen(false);
                    navigate("/admin");
                  }}
                >
                  <Text style={styles.menuIcon}>👤</Text>
                  <Text style={styles.menuText}>Admin</Text>
                </TouchableOpacity>

                {/* Switch User */}
                <TouchableOpacity
                  style={[styles.menuItem, styles.logoutSectionItem]}
                  onPress={handleSwitchUser}
                >
                  <Text style={styles.menuIcon}>🔄</Text>
                  <Text style={styles.menuText}>Switch User</Text>
                </TouchableOpacity>

                <TouchableOpacity
                    style={[styles.menuItem, styles.logoutItem, styles.menuItemLast]}
                  onPress={handleLogoutClick}
                >
                  <Text style={styles.menuIcon}>🚪</Text>
                  <Text style={[styles.menuText, styles.logoutText]}>Logout</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        ) : (
          <View style={styles.infoContainer}>
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.infoButton}
              onPress={() => {
                setInfoOpen(!infoOpen);
              }}
            >
              <Text style={styles.infoIcon}>ℹ️</Text>
            </TouchableOpacity>
            
            {/* Info Dropdown */}
            {infoOpen && shopInfo && (() => {
              return true;
            })() && (
              <View style={styles.infoDropdown}>
                <Text style={styles.infoTitle}>Contact Us</Text>
                
                {/* Address */}
                {shopInfo.address && (
                  <TouchableOpacity
                    style={styles.infoItem}
                    onPress={handleAddressClick}
                  >
                    <Text style={styles.infoIconSmall}>📍</Text>
                    <Text style={styles.infoLink}>{formatAddress(shopInfo.address)}</Text>
                  </TouchableOpacity>
                )}
                
                {/* Phone */}
                {shopInfo.phone && (
                  <TouchableOpacity
                    style={styles.infoItem}
                    onPress={handlePhoneClick}
                  >
                    <Text style={styles.infoIconSmall}>📞</Text>
                    <Text style={styles.infoLink}>{formatPhoneNumber(shopInfo.phone)}</Text>
                  </TouchableOpacity>
                )}
                
                {/* Email */}
                {shopInfo.email && (
                  <TouchableOpacity
                    style={styles.infoItem}
                    onPress={handleEmailClick}
                  >
                    <Text style={styles.infoIconSmall}>✉️</Text>
                    <Text style={styles.infoLink}>{shopInfo.email}</Text>
                  </TouchableOpacity>
                )}

                {/* Login Link for unauthenticated users */}
                <TouchableOpacity
                  style={styles.infoItem}
                  onPress={() => {
                    setInfoOpen(false);
                    navigate("/login");
                  }}
                >
                  <Text style={styles.infoIconSmall}>🔐</Text>
                  <Text style={styles.infoLink}>Staff Login</Text>
                </TouchableOpacity>
              </View>
            )}
          </View>
        )}
        </View>
      </View>

      {/* Helper button bar - authorized users only.
          Suppressed on the booking flows (showHelperBar={false}): mid-booking, "New"
          would discard the draft in progress and the others navigate away entirely. */}
      {isAuthenticated && showHelperBar && (
        <View style={[styles.helperBar, isDesktop && styles.helperBarDesktop]}>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.helperButton, { backgroundColor: "#1F8A5B" }]}
            onPress={() => navigate("/new-booking")}
          >
            <Text style={styles.helperButtonText}>➕ New</Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.helperButton, { backgroundColor: "#B4761B" }]}
            onPress={() => navigate("/today")}
          >
            <Text style={[styles.helperButtonText, { color: "#2E3A38" }]}>📍 Today</Text>
          </TouchableOpacity>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.helperButton, { backgroundColor: "#0E7C6B" }]}
            onPress={() => navigate("/bookings")}
          >
            <Text style={styles.helperButtonText}>📋 All</Text>
          </TouchableOpacity>
        </View>
      )}

      {/* Backdrop to close menu or info */}
      {(menuOpen || infoOpen) && (
        <TouchableOpacity
          style={styles.backdrop}
          activeOpacity={1}
          onPress={() => {
            setMenuOpen(false);
            setInfoOpen(false);
          }}
        />
      )}

      {/* Logout Confirmation Modal */}
      <Modal
        visible={showLogoutConfirm}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowLogoutConfirm(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Confirm Logout</Text>
            <Text style={styles.modalMessage}>
              Are you sure you want to logout?
            </Text>
            <View style={styles.modalButtons}>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={() => setShowLogoutConfirm(false)}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity dataSet={{ hover: "lift" }}
                style={[styles.modalButton, styles.modalButtonConfirm]}
                onPress={handleLogout}
              >
                <Text style={styles.modalButtonConfirmText}>Logout</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  headerWrapper: {
    backgroundColor: "#0F1A19",
    borderBottomWidth: 2,
    borderBottomColor: "#2E3A38",
    position: "relative",
    zIndex: 1000,
  },
  headerWrapperOffline: {
    backgroundColor: "#B4761B",
    borderBottomColor: "#B4761B",
  },
  headerWrapperTestMode: {
    backgroundColor: "#C4453D",
    borderBottomColor: "#C4453D",
  },
  testModeBanner: {
    backgroundColor: "#FFFFFF",
    paddingVertical: 3,
    alignItems: "center",
  },
  testModeBannerText: {
    color: "#C4453D",
    fontSize: 11,
    fontWeight: "bold",
    letterSpacing: 1,
  },
  helperBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
  },
  helperBarDesktop: {
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
  },
  helperButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 16,
    backgroundColor: "#2E3A38",
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
  },
  helperButtonText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerDesktop: {
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
  },
  logo: {
    width: 50,
    height: 50,
    marginRight: 4,
  },
  offlineDataBanner: {
    position: "absolute",
    left: "50%",
    transform: [{ translateX: -75 }], // Center the banner (approximate half of text width)
    backgroundColor: "rgba(0, 0, 0, 0.3)", // Semi-transparent dark overlay
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 5,
    zIndex: 1000,
  },
  offlineDataText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "600",
  },
  titleContainer: {
    flex: 1,
    marginLeft: 12,
    marginRight: 12,
  },
  title: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
  },
  subtitle: {
    color: "#DDE3E2",
    fontSize: 12,
    marginTop: 2,
  },
  offlineIndicator: {
    flexDirection: "column",
    alignItems: "center",
    marginRight: 12,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: "rgba(255, 59, 48, 0.15)",
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#C4453D",
  },
  offlineIcon: {
    fontSize: 16,
    marginBottom: 2,
  },
  offlineText: {
    color: "#C4453D",
    fontSize: 10,
    fontWeight: "600",
  },
  menuContainer: {
    position: "relative",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  userName: {
    color: "#FFFFFF",
    fontSize: 15,
    fontWeight: "600",
    marginRight: 8,
    maxWidth: 260,
  },
  // Lighter than the name so it reads as a qualifier, not part of the name.
  userNameAdmin: {
    color: colors.primarySoft,
    fontWeight: "500",
  },
  bugButton: {
    padding: 8,
    marginRight: 4,
  },
  bugIcon: {
    fontSize: 24,
  },
  infoContainer: {
    position: "relative",
  },
  hamburger: {
    padding: 10,
  },
  hamburgerLine: {
    width: 25,
    height: 3,
    backgroundColor: "#FFFFFF",
    marginVertical: 2,
    borderRadius: 2,
  },
  infoButton: {
    padding: 10,
  },
  infoIcon: {
    fontSize: 24,
  },
  infoIconSmall: {
    fontSize: 20,
  },
  menuDropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 8,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
    zIndex: 1001,
    minWidth: 200,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 15,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  menuItemLast: {
    borderBottomWidth: 0,
  },
  menuIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  menuText: {
    fontSize: 16,
    color: "#2E3A38",
    fontWeight: "500",
  },
  menuSeparator: {
    height: 2,
    backgroundColor: "#8A9794",
    marginVertical: 4,
  },
  logoutSectionItem: {
    borderBottomWidth: 0,
  },
  logoutItem: {
    borderTopWidth: 0,
    marginTop: 0,
  },
  logoutText: {
    color: "#C4453D",
  },
  infoDropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 8,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
    zIndex: 1001,
    minWidth: 280,
    padding: 16,
  },
  infoTitle: {
    fontSize: 17,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
    borderBottomWidth: 2,
    borderBottomColor: "#0E7C6B",
    paddingBottom: 8,
  },
  infoItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#EDF1F0",
  },
  infoLink: {
    fontSize: 14,
    color: "#0E7C6B",
    marginLeft: 12,
    textDecorationLine: "underline",
    flex: 1,
  },
  backdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "transparent",
    zIndex: 999,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalContent: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 24,
    width: "80%",
    maxWidth: 400,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 8,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 12,
  },
  modalMessage: {
    fontSize: 16,
    color: "#5C6B68",
    marginBottom: 24,
  },
  modalButtons: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 12,
  },
  modalButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    minWidth: 100,
    alignItems: "center",
  },
  modalButtonCancel: {
    backgroundColor: "#EDF1F0",
  },
  modalButtonConfirm: {
    backgroundColor: "#C4453D",
  },
  modalButtonCancelText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
  },
  modalButtonConfirmText: {
    fontSize: 16,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});

export default AuthHeader;

